import { useEffect, useRef, useState } from "react";
import { sendAssistantMessage, resumeAssistant, startAssistantConversation } from "./selfcareClient";
import { getToken } from "../../../utils/tokenStorage";

const KEY = "selfcareConversationId";

export default function useSelfcareAssistant(onSessionChange) {
    const token = getToken();
    const sessionToken = useRef(token);
    const conversation = useRef(sessionStorage.getItem(KEY));
    const busy = useRef(false);
    const pending = useRef(null);
    const [files, setFiles] = useState([]);
    const [state, setState] = useState(null);

    useEffect(() => {
        if (sessionToken.current !== token) {
            sessionToken.current = token;
            conversation.current = null;
            pending.current = null;
            sessionStorage.removeItem(KEY);
            setFiles([]);
            setState(null);
            onSessionChange?.();
        }
    }, [token, onSessionChange]);

    const apply = result => {
        if (result.conversation) {
            conversation.current = result.conversation.conversationId;
            sessionStorage.setItem(KEY, conversation.current);
            setState(result.conversation.state);
        }
        return result;
    };

    const send = async text => {
        if (busy.current) return null;
        busy.current = true;
        // On a transport failure, retain the same message ID. Never auto-retry POST.
        const same = pending.current?.text === text;
        if (!same && pending.current) {
            busy.current = false;
            throw new Error("Le dernier envoi n'a pas reçu de réponse. Renvoyez le même texte pour récupérer son résultat, ou consultez Mes attestations.");
        }
        const id = pending.current?.id || crypto.randomUUID();
        const sentWithToken = getToken();
        pending.current = { id, text };
        try {
            if (!conversation.current) {
                const started = await startAssistantConversation();
                if (getToken() !== sentWithToken) throw new Error("La session a changé.");
                apply(started);
            }
            const result = await sendAssistantMessage(text, conversation.current, id, files);
            if (getToken() !== sentWithToken) throw new Error("La session a changé. Le résultat de la session précédente n'est pas affiché.");
            pending.current = null;
            if (["DONE", "CANCELLED", "CREATED_UPLOAD_PENDING"].includes(result.conversation?.state)) setFiles([]);
            return apply(result);
        } catch (error) {
            if (error.status) pending.current = null;
            throw error;
        } finally {
            busy.current = false;
        }
    };

    const resume = async () => {
        if (!conversation.current) return null;
        const sentWithToken = getToken();
        const result = await resumeAssistant(conversation.current);
        if (getToken() !== sentWithToken) throw new Error("La session a changé.");
        return apply(result);
    };
    const reset = () => {
        if (busy.current) return;
        conversation.current = null;
        pending.current = null;
        sessionStorage.removeItem(KEY);
        setState(null);
        setFiles([]);
    };
    return { send, resume, reset, files, setFiles, state };
}
